import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, X, ShoppingCart, Palette, Sparkles, Upload, CheckCircle2, Shirt, PenTool, ArrowRight, Shield, Clock, Leaf, Eye, Layers, Zap, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [sortBy, setSortBy] = useState("name");
  const { addToCart } = useCart();
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { category: selectedCategory || undefined, search: searchQuery || undefined } }),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch {
      toast.error("Erreur lors du chargement des produits");
    }
    setLoading(false);
  };

  const handleAddToCart = async (product) => {
    if (!token) { toast.error("Connectez-vous pour ajouter au panier"); return; }
    const result = await addToCart(product.id);
    if (result.error) { toast.error(result.error); } else { toast.success(`${product.name} ajouté au panier`); }
  };

  const formatPrice = (price) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(price);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case "price-asc": return a.price - b.price;
      case "price-desc": return b.price - a.price;
      default: return a.name.localeCompare(b.name);
    }
  });

  const clearFilters = () => { setSelectedCategory(""); setSearchQuery(""); setSearchParams({}); };
  const hasActiveFilters = selectedCategory || searchQuery;

  const techniques = [
    {
      name: "Sérigraphie",
      image: "https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/9ea6c27753d781a8263fee3f62e965d846f89a773ba94113c16434e514dfa515.png",
      desc: "Idéale pour les grandes séries. Encre poussée à travers un écran pour un rendu vif et durable sur textile et objets.",
      best: "T-shirts, sacs, textiles",
    },
    {
      name: "Broderie",
      image: "https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/bf88f79c5d0bf660ccee468d5789072afe51127e9a34f9a63e26bebfca4ec498.png",
      desc: "Le marquage premium par excellence. Fils brodés directement sur le textile pour un rendu haut de gamme et résistant.",
      best: "Polos, casquettes, vestes",
    },
    {
      name: "Gravure laser",
      image: "https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/04472d046e8d7942eacf406b2067126811f32672beb0b5551da5958e8c00c1c4.png",
      desc: "Précision extrême sur métal, bois et verre. La gravure crée un marquage permanent, élégant et indélébile.",
      best: "Gourdes, stylos, objets métal",
    },
    {
      name: "Impression numérique",
      image: "https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/55f6ef54ad2e490faf795ef0feebd4b0687063fb51aac2563db97e97c3787caa.png",
      desc: "Reproduction fidèle de visuels complexes en quadrichromie. Idéale pour les logos multicolores et les photos.",
      best: "Mugs, coques, textiles blancs",
    },
  ];

  const guarantees = [
    { icon: Eye, title: "BAT systématique", desc: "Un Bon à Tirer vous est envoyé avant chaque fabrication pour validation" },
    { icon: Shield, title: "Qualité garantie", desc: "Produits certifiés et contrôle qualité à chaque étape de production" },
    { icon: Leaf, title: "Options éco-responsables", desc: "Coton bio, matériaux recyclés, encres écologiques disponibles" },
    { icon: Clock, title: "Livraison 2-4 semaines", desc: "Délais maîtrisés avec suivi en temps réel de votre commande" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="shop-page">
      {/* Hero Banner */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3557] via-[#2A4A6D] to-[#1D3557]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#8C9D78] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C65D47] rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 mb-6">
                <Palette className="w-4 h-4" />
                <span className="text-sm font-medium">100% Personnalisable</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
                Votre marque sur des <span className="text-[#8C9D78]">objets uniques</span>
              </h1>
              <p className="text-xl text-white/80 max-w-xl mb-8">
                T-shirts, goodies, textile, accessoires... Uploadez votre logo et visualisez-le 
                en temps réel sur nos produits avant de commander.
              </p>
              <div className="max-w-xl">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="text"
                    placeholder="Rechercher un produit (t-shirt, sac, stylo...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 rounded-full text-lg bg-white border-0 shadow-xl"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/1ab5e710b5dc97d1763393d5c0eaed0507163b9fb84280f2c0733a5caa5e4012.png"
                alt="Objets personnalisés"
                className="rounded-3xl shadow-2xl object-cover"
                style={{ maxHeight: 440 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Personalization Steps */}
      <section className="py-16 bg-[#8C9D78]/10 border-b border-[#8C9D78]/20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              <Sparkles className="inline w-8 h-8 text-[#8C9D78] mr-2" />
              Personnalisez en 3 étapes
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Votre logo, votre identité, sur tous vos supports. Du t-shirt au stylo, créez des objets uniques qui vous ressemblent.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Upload, title: "Envoyez votre logo", desc: "Téléchargez votre visuel en haute qualité (PNG, JPG, SVG)" },
              { icon: PenTool, title: "Prévisualisez le rendu", desc: "Positionnez, redimensionnez et validez le rendu en temps réel" },
              { icon: CheckCircle2, title: "Validez et commandez", desc: "Un BAT vous est envoyé avant fabrication pour validation finale" },
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <step.icon className="w-10 h-10 text-[#8C9D78]" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#1D3557] text-white flex items-center justify-center text-sm font-bold" style={{ left: 'calc(50% + 20px)' }}>
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-semibold text-[#1D3557] mb-2">{step.title}</h3>
                <p className="text-stone-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <div className="container-custom py-12">
        <div className="text-center mb-10">
          <span className="text-sm font-semibold text-[#8C9D78] uppercase tracking-widest">Notre Catalogue</span>
          <h2 className="text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Choisissez, personnalisez, commandez
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-28 space-y-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#1D3557] mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Catégories
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => { setSelectedCategory(""); setSearchParams({}); }}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${!selectedCategory ? "bg-[#8C9D78] text-white shadow-md" : "hover:bg-stone-100 text-stone-700"}`}
                    data-testid="category-all"
                  >
                    Tous les produits
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => { setSelectedCategory(cat.id); setSearchParams({ category: cat.id }); }}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${selectedCategory === cat.id ? "bg-[#8C9D78] text-white shadow-md" : "hover:bg-stone-100 text-stone-700"}`}
                      data-testid={`category-${cat.slug}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#8C9D78] flex-shrink-0">
                    <img src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/wpa826g4_%40PARLACOM-MAUD-34.jpg" alt="Maud Dupupet" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#8C9D78]">Besoin d'un conseil ?</h4>
                    <p className="text-white/70 text-sm">Maud vous accompagne</p>
                  </div>
                </div>
                <p className="text-white/70 text-sm mb-4">
                  Je vous aide à choisir les objets personnalisés parfaits pour votre communication.
                </p>
                <Button asChild variant="secondary" className="w-full bg-white text-[#1D3557] hover:bg-stone-100 rounded-full">
                  <Link to="/contact">Me contacter</Link>
                </Button>
              </div>

              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline" className="w-full rounded-full" data-testid="clear-filters">
                  <X className="w-4 h-4 mr-2" />
                  Effacer les filtres
                </Button>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden rounded-full" data-testid="mobile-filter-btn">
                      <Filter className="w-4 h-4 mr-2" />
                      Filtres
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <SheetHeader><SheetTitle>Filtres</SheetTitle></SheetHeader>
                    <div className="mt-6 space-y-6">
                      <div>
                        <h3 className="font-semibold text-[#1D3557] mb-3">Rechercher</h3>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                          <Input type="text" placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1D3557] mb-3">Catégories</h3>
                        <div className="space-y-2">
                          <button onClick={() => setSelectedCategory("")} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!selectedCategory ? "bg-[#8C9D78] text-white" : ""}`}>Tous</button>
                          {categories.map((cat) => (
                            <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedCategory === cat.id ? "bg-[#8C9D78] text-white" : ""}`}>{cat.name}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
                <p className="text-stone-500"><span className="font-semibold text-[#1D3557]">{sortedProducts.length}</span> produits à personnaliser</p>
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 rounded-full" data-testid="sort-select"><SelectValue placeholder="Trier par" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nom A-Z</SelectItem>
                  <SelectItem value="price-asc">Prix croissant</SelectItem>
                  <SelectItem value="price-desc">Prix décroissant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedCategory && (
                  <Badge className="px-4 py-2 bg-[#8C9D78] text-white rounded-full">
                    {categories.find(c => c.id === selectedCategory)?.name}
                    <button onClick={() => setSelectedCategory("")} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
                  </Badge>
                )}
                {searchQuery && (
                  <Badge className="px-4 py-2 bg-[#1D3557] text-white rounded-full">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery("")} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
                  </Badge>
                )}
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="aspect-square skeleton rounded-2xl" />
                    <div className="skeleton h-6 w-3/4 rounded" />
                    <div className="skeleton h-4 w-1/2 rounded" />
                  </div>
                ))}
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl" data-testid="no-products">
                <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-stone-300" />
                </div>
                <h3 className="text-xl font-semibold text-[#1D3557] mb-2">Aucun produit trouvé</h3>
                <p className="text-stone-500 mb-6">Essayez de modifier vos critères de recherche</p>
                <Button onClick={clearFilters} className="rounded-full bg-[#8C9D78] hover:bg-[#7A8B69]">Voir tous les produits</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
                {sortedProducts.map((product) => (
                  <div key={product.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`product-${product.id}`}>
                    <Link to={`/boutique/${product.id}`} className="block">
                      <div className="relative aspect-square overflow-hidden bg-stone-100">
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1D3557]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                          <span className="text-white font-medium flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Personnaliser avec mon logo
                          </span>
                        </div>
                        {product.customizable && (
                          <span className="absolute top-3 left-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8C9D78] text-white">
                            <Palette className="w-3 h-3 mr-1" />
                            Personnalisable
                          </span>
                        )}
                      </div>
                    </Link>
                    <div className="p-5">
                      <Link to={`/boutique/${product.id}`}>
                        <h3 className="font-semibold text-[#1D3557] group-hover:text-[#8C9D78] transition-colors line-clamp-2 mb-2">{product.name}</h3>
                      </Link>
                      <p className="text-sm text-stone-500 mb-3 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          {product.pricing_tiers?.length > 0 ? (
                            <>
                              <p className="text-xs text-[#C65D47] font-medium">Prix de vente indicatif à partir de</p>
                              <p className="text-xl font-bold text-[#1D3557]">{formatPrice(Math.min(...product.pricing_tiers.map(t => t.price)))}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-xl font-bold text-[#1D3557]">{formatPrice(product.price)}</p>
                              <p className="text-xs text-stone-500">à partir de {product.min_quantity} unités</p>
                            </>
                          )}
                        </div>
                        <Button
                          onClick={(e) => { e.preventDefault(); handleAddToCart(product); }}
                          size="icon"
                          className="rounded-full bg-[#8C9D78] hover:bg-[#7A8B69] shadow-md"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Before/After Showcase */}
      <section className="py-20" data-testid="before-after-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/2eae383f40ae5552ae8670881d7ddd1cbd4e2640768dd7c7f17743a6a845f089.png"
                alt="Avant / Après personnalisation"
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="space-y-6">
              <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Le pouvoir de la personnalisation</span>
              <h2 className="text-4xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                D'un objet vierge à un <span className="text-[#8C9D78]">ambassadeur de votre marque</span>
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                Chaque objet publicitaire est une opportunité de marquer les esprits. 
                Un simple t-shirt blanc devient un support de communication puissant 
                dès qu'il porte vos couleurs et votre identité. 
                Nos techniques de marquage professionnel garantissent un rendu impeccable 
                qui valorise votre image.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "+10 000", label: "références disponibles" },
                  { value: "4", label: "techniques de marquage" },
                  { value: "100%", label: "personnalisable" },
                  { value: "BAT", label: "avant fabrication" },
                ].map((stat, i) => (
                  <div key={i} className="bg-[#FDFBF7] border border-stone-200 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-bold text-[#1D3557]">{stat.value}</p>
                    <p className="text-xs text-stone-500">{stat.label}</p>
                  </div>
                ))}
              </div>
              <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full px-8 h-12 text-base font-semibold shadow-lg">
                <Link to="/contact">
                  Demander un devis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Techniques de marquage */}
      <section className="py-20 bg-[#1D3557]" data-testid="techniques-section">
        <div className="container-custom">
          <div className="text-center mb-14">
            <span className="text-[#8C9D78] font-semibold uppercase tracking-widest text-sm">Savoir-faire</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Nos techniques de marquage
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Chaque produit mérite la technique adaptée. Nous maîtrisons 4 procédés 
              pour un rendu optimal selon le support et votre budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {techniques.map((tech, i) => (
              <div key={i} className="group bg-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/10 hover:border-[#8C9D78]/40 transition-all">
                <div className="grid sm:grid-cols-2">
                  <div className="aspect-square sm:aspect-auto overflow-hidden">
                    <img src={tech.image} alt={tech.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-6 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8C9D78]/20 text-[#8C9D78] mb-3 w-fit">
                      <Layers className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">{tech.name}</span>
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed mb-3">{tech.desc}</p>
                    <p className="text-white/50 text-xs">
                      <span className="text-[#8C9D78] font-semibold">Idéal pour :</span> {tech.best}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-16 bg-[#F4F1EA]" data-testid="guarantees-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {guarantees.map((g, i) => (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:shadow-lg transition-all">
                  <g.icon className="w-8 h-8 text-[#8C9D78]" />
                </div>
                <h3 className="font-semibold text-[#1D3557] text-sm mb-1">{g.title}</h3>
                <p className="text-stone-500 text-xs leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-[#8C9D78]">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-white text-center lg:text-left">
              <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                Vous ne trouvez pas ce que vous cherchez ?
              </h2>
              <p className="text-white/80 max-w-xl">
                Avec nos partenaires, nous avons accès à plus de 10 000 références. 
                Décrivez-nous votre besoin et nous vous proposerons la solution idéale.
              </p>
            </div>
            <Button asChild className="bg-[#1D3557] hover:bg-[#152A45] text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg">
              <Link to="/contact">
                <Shirt className="w-5 h-5 mr-2" />
                Demander un devis personnalisé
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
